"use client";

import { useAuth } from "@/components/AuthContext";
import { PaywallExperimentPricingCards } from "@/components/paywall/PaywallExperimentPricingCards";
import { PricingCard } from "@/components/paywall/PricingCard";
import { usePaywallViewTracking } from "@/hooks/usePaywallViewTracking";
import { useRevenueCatCheckoutAttempt } from "@/hooks/useRevenueCatCheckoutAttempt";
import { invalidateRevenueCatSubscriptionCache } from "@/hooks/useRevenueCatSubscription";
import { trackBeginCheckout, trackRevenueCatConversionEvent } from "@/lib/analytics/gtm-datalayer";
import { normalizePaywallSource } from "@/lib/analytics/paywall-source";
import { trackEventWithAttribution, trackUpgradeIntent } from "@/lib/analytics/posthog-helpers";
import { buildOpenAIAdsPurchaseMetadata, getAttributionData } from "@/lib/analytics/utm-tracker";
import {
  PAYWALL_GUIDED_ANCHOR_EXPERIMENT_KEY,
  PAYWALL_GUIDED_ANCHOR_LAYOUTS,
  type PaywallGuidedAnchorVariant,
} from "@/lib/experiments/paywallGuidedAnchor";
import { enrichRevenueCatMetadataWithInsertAffiliate } from "@/lib/insert-affiliate";
import { createLogger } from "@/lib/logger";
import {
  OfferMode,
  UserIntentSegment,
  classifyUserIntentFromStats,
  recommendedOfferModeForSegment,
} from "@/lib/paywall/user-intent";
import { buildCheckoutSubscriberAttributes } from "@/lib/revenuecat/checkout-attributes";
import {
  CHECKOUT_TELEMETRY_VERSION,
  CheckoutAttempt,
  CheckoutStage,
} from "@/lib/revenuecat/checkout-telemetry";
import {
  getOfferings,
  getRevenueCatAppUserId,
  initializeRevenueCat,
  initializeRevenueCatAnonymous,
  purchasePackage,
  setAttributes,
} from "@/lib/revenuecat/client";
import { Currency, PackageType, REVENUECAT_CONFIG } from "@/lib/revenuecat/config";
import { detectUserCurrency } from "@/lib/revenuecat/currency-detector";
import {
  findSelectableRevenueCatPackage,
  formatMonthlyEquivalent,
  getComparableAnnualSavingsPercent,
  getFallbackPackagePriceValue,
  getPackageDisplayPricing,
  getRevenueCatPackageCurrency,
  getRevenueCatPackagePriceAmount,
} from "@/lib/revenuecat/package-pricing";
import { getPromoDisplayState } from "@/lib/revenuecat/promo-display";
import {
  ANONYMOUS_PURCHASE_ACTIVATION_PATH,
  clearPendingWebPurchase,
  storePendingWebPurchase,
} from "@/lib/revenuecat/redemption";
import { getPaywallLoadErrorMessage } from "@/lib/revenuecat/user-facing-errors";
import { motion, useReducedMotion } from "framer-motion";
import { useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";

const logger = createLogger("PricingSection");

interface PricingSectionProps {
  showHeader?: boolean;
  headerTitle?: string;
  headerSubtitle?: string;
  onPurchaseSuccess?: () => void;
  redirectOnNoAuth?: string;
  buildRedirectOnNoAuth?: (packageType: PackageType, coupon: string | null) => string;
  resumePackage?: PackageType | null;
  analyticsProperties?: Record<string, unknown>;
  onAuthRequired?: (packageType: PackageType) => void;
  requestAnonymousCheckout?: (
    packageType: PackageType
  ) => Record<string, string | number | boolean> | null;
  /** If true, redirects to auth immediately if not logged in. If false, shows pricing and redirects on purchase click. */
  requireAuthUpfront?: boolean;
  analyticsSource?: string;
  pricingCardVariant?: "default" | "zen" | "landing";
  paywallExperiment?: {
    status: "loading" | "resolved";
    variant: PaywallGuidedAnchorVariant;
  };
  cohortLanguageCode?: string | null;
  onCohortAuthRequired?: () => void;
  onExperimentOffersRendered?: (context: {
    offerMode: OfferMode;
    userIntentSegment: UserIntentSegment;
  }) => void;
}

export function PricingSection({
  showHeader = true,
  headerTitle = "Choose Your Plan",
  headerSubtitle = "All plans include unlimited conversations, all languages, and full access to features",
  onPurchaseSuccess,
  redirectOnNoAuth = "/auth?redirect=/paywall",
  buildRedirectOnNoAuth,
  resumePackage = null,
  analyticsProperties,
  onAuthRequired,
  requestAnonymousCheckout,
  requireAuthUpfront = true,
  analyticsSource = "pricing_section",
  pricingCardVariant = "default",
  paywallExperiment,
  cohortLanguageCode,
  onCohortAuthRequired,
  onExperimentOffersRendered,
}: PricingSectionProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, isLoading: authLoading } = useAuth();
  const posthog = usePostHog();
  const shouldReduceMotion = useReducedMotion();
  const isCompactLanding = pricingCardVariant === "landing" && !showHeader;

  const checkout = useRevenueCatCheckoutAttempt(posthog);
  const resumedPackageRef = useRef<PackageType | null>(null);
  const resumeCheckoutActionRef = useRef<(packageType: PackageType) => Promise<void>>(
    async () => {}
  );
  const [isInitializing, setIsInitializing] = useState(true);
  const [offerMode, setOfferMode] = useState<OfferMode>("trial");
  const [userIntentSegment, setUserIntentSegment] = useState<UserIntentSegment>("cold_user");
  const [isSegmentResolved, setIsSegmentResolved] = useState(false);
  const [hasExternalOfferOverride, setHasExternalOfferOverride] = useState(false);
  const [hasAppliedSegmentDefault, setHasAppliedSegmentDefault] = useState(false);
  const [externalCouponProcessed, setExternalCouponProcessed] = useState(false);
  const [lastDiscountCode, setLastDiscountCode] = useState("birdsofafeather");
  const [offerings, setOfferings] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState<Currency>("USD");
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [selectedOfferingKey, setSelectedOfferingKey] = useState<string | null>(null);
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [showOfferToggleOnPromo, setShowOfferToggleOnPromo] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState<PackageType>("monthly");
  const paywallSource = normalizePaywallSource(analyticsSource);
  const pricingSectionRef = usePaywallViewTracking<HTMLDivElement>({
    enabled:
      !authLoading &&
      !isInitializing &&
      Boolean(offerings) &&
      isSegmentResolved &&
      (!paywallExperiment || paywallExperiment.status === "resolved"),
    offerMode,
    source: paywallSource,
    surface: "pricing_section",
    userIntentSegment,
    userType: user ? "authenticated" : "guest",
    posthog: analyticsProperties ? posthog : undefined,
    posthogProperties: analyticsProperties,
  });

  // Detect user currency
  useEffect(() => {
    async function detectCurrency() {
      try {
        const detectedCurrency = await detectUserCurrency();
        setCurrency(detectedCurrency);
        logger.info("Currency detected", { currency: detectedCurrency });
      } catch (err) {
        logger.warn("Failed to detect currency, using USD", { error: err });
        setCurrency("USD");
      }
    }
    detectCurrency();
  }, []);

  // Initialize RevenueCat
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    async function initialize() {
      // Wait for auth to finish loading
      if (authLoading) {
        logger.info("Waiting for authentication to load...");
        return;
      }

      // If no user after auth loads
      if (!user) {
        if (requireAuthUpfront) {
          // Redirect to auth page immediately
          logger.warn("No authenticated user, redirecting to auth");
          router.push(redirectOnNoAuth);
          return;
        } else {
          // Allow viewing pricing without auth - will redirect on purchase click
          // Initialize with anonymous ID to fetch offerings
          logger.info("No authenticated user, initializing with anonymous ID for pricing view");
          try {
            await initializeRevenueCatAnonymous();
            const fetchedOfferings = await getOfferings();
            setOfferings(fetchedOfferings);
            logger.info("Pricing loaded for anonymous user", {
              offeringsCount: fetchedOfferings.all ? Object.keys(fetchedOfferings.all).length : 0,
            });
          } catch (err: any) {
            logger.error("Failed to load offerings for anonymous user", { error: err });
            setError(getPaywallLoadErrorMessage());
          } finally {
            setIsInitializing(false);
          }
          return;
        }
      }

      try {
        // Always use email as the app user ID for RevenueCat
        const userId = user.email;

        if (!userId) {
          throw new Error("No email available for RevenueCat initialization");
        }

        logger.info("Initializing RevenueCat", {
          appUserId: userId,
          userIdFromAuth: user.id,
          detectedCurrency: currency,
        });

        await initializeRevenueCat(userId);
        const fetchedOfferings = await getOfferings();
        setOfferings(fetchedOfferings);

        logger.info("Pricing section initialized", {
          userId,
          currency,
          offeringsCount: fetchedOfferings.all ? Object.keys(fetchedOfferings.all).length : 0,
          hasCurrentOffering: !!fetchedOfferings.current,
          currentOfferingId: fetchedOfferings.current?.identifier,
          availablePackagesCount: fetchedOfferings.current?.availablePackages?.length || 0,
          packageIdentifiers:
            fetchedOfferings.current?.availablePackages?.map((p: any) => p.identifier) || [],
        });
      } catch (err: any) {
        logger.error("Failed to initialize pricing section", { error: err });
        setError(getPaywallLoadErrorMessage());
      } finally {
        setIsInitializing(false);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    }

    // Set up auth timeout: redirect to auth if authentication takes too long (only if requireAuthUpfront)
    if (requireAuthUpfront && authLoading && !timeoutId) {
      timeoutId = setTimeout(() => {
        logger.warn("Auth taking too long, redirecting to auth page");
        router.push(redirectOnNoAuth);
      }, 3000); // 3 second timeout
    }

    initialize();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [user, authLoading, router, currency, redirectOnNoAuth, requireAuthUpfront]);

  function findDiscountOfferingKey(
    all: Record<string, any> | undefined | null,
    couponCode: string
  ) {
    if (!all) return null;
    const code = couponCode.toLowerCase();
    const entries = Object.entries(all);

    // Any coupon ending in 33 uses the discount offering
    if (code.endsWith("33")) {
      for (const [key, off] of entries) {
        const id = (off?.identifier ?? "").toString();
        if (id === "botes33_offering" || id === "ofrng3dc99cd0fa") {
          return key;
        }
      }
      // Fallback: match any offering that clearly represents a 33% promo.
      for (const [key, off] of entries) {
        const id = (off?.identifier ?? "").toString().toLowerCase();
        const desc = (off?.serverDescription ?? "").toString().toLowerCase();
        if (id.includes("33") || desc.includes("33")) {
          return key;
        }
      }
      return null;
    }

    // Birdsofafeather logic (default)
    for (const [key, off] of entries) {
      const id = (off?.identifier ?? "").toString();
      const desc = (off?.serverDescription ?? "").toString();
      if (
        id.toLowerCase() === "chicky_subscription_20_off" ||
        id.toLowerCase().includes("20_off") ||
        id.toLowerCase().includes("20% off") ||
        desc.toLowerCase().includes("20% off") ||
        id === "ofrng3050721c6b"
      ) {
        return key;
      }
    }
    return null;
  }

  function getActiveOffering() {
    if (!offerings) return null;
    if (couponApplied && selectedOfferingKey && offerings.all?.[selectedOfferingKey]) {
      return offerings.all[selectedOfferingKey];
    }
    return offerings.current ?? null;
  }

  function resetToTrialMode() {
    setOfferMode("trial");
    setCoupon("");
    setCouponApplied(false);
    setSelectedOfferingKey(null);
  }

  async function applyCoupon(overrideCode?: string): Promise<boolean> {
    setError(null);
    const code = (overrideCode ?? coupon).trim().toLowerCase();

    if (overrideCode) {
      setCoupon(overrideCode);
    }

    // Accept any coupon ending in 33, or birdsofafeather or savestate
    if (code !== "birdsofafeather" && code !== "savestate" && !code.endsWith("33")) {
      setCouponApplied(false);
      setSelectedOfferingKey(null);
      setError("That code isn't valid. Check it and try again.");
      return false;
    }

    if (!offerings) {
      setError("The plans are still loading. Please try again in a moment.");
      return false;
    }

    const key = findDiscountOfferingKey(offerings.all, code);
    if (!key) {
      setError("That discount isn't available right now. Choose another plan or contact support.");
      return false;
    }

    setSelectedOfferingKey(key);
    setCouponApplied(true);
    setLastDiscountCode(code);

    return true;
  }

  // Handle coupon from URL parameter or Cookie
  useEffect(() => {
    const couponFromUrl = searchParams?.get("coupon");
    let couponToApply = couponFromUrl;

    // If no URL coupon, check cookie
    if (!couponToApply && typeof document !== "undefined") {
      const match = document.cookie.match(new RegExp("(^| )coupon_code=([^;]+)"));
      if (match) {
        couponToApply = match[2];
      }
    }

    const normalizedCoupon = (() => {
      if (!couponToApply) return null;
      let value = couponToApply;
      try {
        value = decodeURIComponent(value);
      } catch {
        // Keep raw value if decoding fails.
      }
      const trimmed = value.trim();
      return trimmed || null;
    })();

    if (normalizedCoupon) {
      setHasExternalOfferOverride(true);
      setHasAppliedSegmentDefault(true);
    }

    if (normalizedCoupon && !couponApplied && !externalCouponProcessed) {
      setCoupon(normalizedCoupon);
      setShowCouponInput(true);
      logger.info("Coupon detected", {
        coupon: normalizedCoupon,
        source: couponFromUrl ? "url" : "cookie",
      });

      // Auto-apply if it's a valid coupon (ends in 33 or is birdsofafeather or savestate)
      const urlCode = normalizedCoupon.toLowerCase();
      const isValidCoupon =
        urlCode === "birdsofafeather" || urlCode === "savestate" || urlCode.endsWith("33");

      if (!isValidCoupon) {
        setExternalCouponProcessed(true);
        return;
      }

      // Wait for offerings to load before marking coupon processing complete.
      if (!offerings) return;

      setExternalCouponProcessed(true);
      void applyCoupon(normalizedCoupon).then((applied) => {
        setOfferMode(applied ? "discount" : "trial");
      });
    }
  }, [searchParams, couponApplied, offerings, externalCouponProcessed]);

  // Segment user intent from Supabase-backed conversation stats.
  useEffect(() => {
    let isCancelled = false;

    async function resolveSegment() {
      if (authLoading) return;
      if (!user) {
        setUserIntentSegment("cold_user");
        setIsSegmentResolved(true);
        return;
      }

      try {
        const response = await fetch("/api/progress/conversations-stats", {
          method: "GET",
          credentials: "include",
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(`stats_http_${response.status}`);
        }

        const stats = await response.json();
        const segment = classifyUserIntentFromStats(stats);
        if (!isCancelled) {
          setUserIntentSegment(segment);
          logger.info("Pricing section user segment resolved", {
            segment,
            sessions: stats?.sessions,
            sessionsThisWeek: stats?.sessionsThisWeek,
            totalMessages: stats?.totalMessages,
            lastActivityAt: stats?.lastActivityAt,
          });
        }
      } catch (err) {
        if (!isCancelled) {
          setUserIntentSegment("cold_user");
          logger.warn("Falling back to cold_user segment", { error: err });
        }
      } finally {
        if (!isCancelled) {
          setIsSegmentResolved(true);
        }
      }
    }

    resolveSegment();

    return () => {
      isCancelled = true;
    };
  }, [authLoading, user]);

  // Auto-select default offer mode after segment resolution unless coupon is externally forced.
  useEffect(() => {
    if (!isSegmentResolved || !offerings || hasAppliedSegmentDefault || hasExternalOfferOverride) {
      return;
    }

    const recommendedMode = recommendedOfferModeForSegment(userIntentSegment);

    if (recommendedMode === "trial") {
      resetToTrialMode();
      setHasAppliedSegmentDefault(true);
      return;
    }

    void applyCoupon(lastDiscountCode).then((applied) => {
      setOfferMode(applied ? "discount" : "trial");
      setHasAppliedSegmentDefault(true);
    });
  }, [
    isSegmentResolved,
    offerings,
    hasAppliedSegmentDefault,
    hasExternalOfferOverride,
    userIntentSegment,
    lastDiscountCode,
  ]);

  const handleSwitchToTrial = () => {
    setHasAppliedSegmentDefault(true);
    resetToTrialMode();
  };

  const handleSwitchToDiscount = async () => {
    setHasAppliedSegmentDefault(true);
    const applied = await applyCoupon(lastDiscountCode);
    setOfferMode(applied ? "discount" : "trial");
  };

  const completeRecoveredCheckout = (attempt: CheckoutAttempt) => {
    if (!checkout.finishAttempt(attempt.id, "succeeded")) return false;
    if (user?.email) {
      invalidateRevenueCatSubscriptionCache(user.email);
    }
    if (!user?.email) {
      router.push(ANONYMOUS_PURCHASE_ACTIVATION_PATH);
      return true;
    }
    if (onPurchaseSuccess) {
      onPurchaseSuccess();
    } else {
      router.push("/success?source=purchase_recovery");
    }
    return true;
  };

  const handleCheckoutPending = (attempt: CheckoutAttempt) => {
    setError(null);
    void checkout.handlePendingPurchase(attempt).then((resolution) => {
      if (resolution.status === "active") {
        completeRecoveredCheckout(attempt);
      } else if (resolution.message) {
        setError(resolution.message);
      }
    });
  };

  const handleVerifyCheckout = async () => {
    const attempt = checkout.getCurrentAttempt();
    if (!attempt) return;

    const resolution = await checkout.verifyCurrentAttempt();
    if (resolution.status === "active") {
      completeRecoveredCheckout(attempt);
    } else if (resolution.message) {
      setError(resolution.message);
    }
  };

  const handleSelectPackage = async (packageType: PackageType, trackIntent = true) => {
    if (trackIntent) {
      trackUpgradeIntent(posthog, analyticsSource, {
        ...analyticsProperties,
        user_id: user?.id,
        user_type: user ? "authenticated" : "guest",
        package: packageType,
        offer_mode: offerMode,
        paywall_source: paywallSource,
        user_intent_segment: userIntentSegment,
      });
    }

    const anonymousCheckoutProperties = !user?.email
      ? (requestAnonymousCheckout?.(packageType) ?? null)
      : null;
    const isAnonymousCheckout = !user?.email && Boolean(anonymousCheckoutProperties);

    if (!user?.email && !isAnonymousCheckout) {
      onAuthRequired?.(packageType);
      // Preserve the explicit plan choice inside the post-auth destination when supported.
      let authUrl = buildRedirectOnNoAuth?.(packageType, coupon || null) ?? redirectOnNoAuth;
      if (!buildRedirectOnNoAuth && coupon) {
        const separator = authUrl.includes("?") ? "&" : "?";
        authUrl = `${authUrl}${separator}coupon=${encodeURIComponent(coupon)}`;
      }
      router.push(authUrl);
      return;
    }

    const attempt = checkout.beginAttempt({
      packageType,
      offerMode,
      userIntentSegment,
      surface: "pricing_section",
      analyticsProperties: {
        ...analyticsProperties,
        ...anonymousCheckoutProperties,
        paywall_source: paywallSource,
        purchase_identity: isAnonymousCheckout ? "anonymous" : "identified",
      },
    });
    if (!attempt) return;

    let stage: CheckoutStage = "package_validation";
    setError(null);

    try {
      logger.info("Initiating purchase", {
        packageType,
        purchaseIdentity: isAnonymousCheckout ? "anonymous" : "identified",
      });

      // Get the package from the active offering (discount if applied)
      const activeOffering = getActiveOffering();
      if (!activeOffering) {
        throw new Error("No offerings available. Please refresh the page.");
      }

      const packageIdentifier = REVENUECAT_CONFIG.packages[packageType].identifier;
      const packageToPurchase = findSelectableRevenueCatPackage({
        activeOffering,
        coupon,
        couponApplied,
        offerings,
        packageType,
        preferredCurrency: currency,
      });

      if (!packageToPurchase) {
        logger.error("Package not found in offerings", {
          packageType,
          packageIdentifier,
          availablePackages: activeOffering.availablePackages?.map((p: any) => p.identifier),
        });
        throw new Error(`Package ${packageType} not found. Please contact support.`);
      }

      logger.info("Found package, initiating purchase", {
        packageIdentifier,
        packageType,
        usingDiscount: couponApplied,
        offeringKey: couponApplied ? selectedOfferingKey : offerings?.current?.identifier,
        couponCode: couponApplied ? coupon : undefined,
      });

      const checkoutValue =
        getRevenueCatPackagePriceAmount(packageToPurchase) ??
        getFallbackPackagePriceValue(packageType, currency);
      const checkoutCurrency = getRevenueCatPackageCurrency(packageToPurchase, currency);
      const checkoutItemId =
        packageToPurchase?.webBillingProduct?.identifier ||
        packageToPurchase?.rcBillingProduct?.identifier ||
        packageToPurchase?.product?.identifier ||
        packageIdentifier;
      trackBeginCheckout({
        value: checkoutValue,
        currency: checkoutCurrency,
        surface: "pricing_section",
        offerMode,
        paywallSource,
        userType: isAnonymousCheckout ? "guest" : "authenticated",
        items: [
          {
            itemId: checkoutItemId,
            itemName: REVENUECAT_CONFIG.packages[packageType].displayName,
            price: checkoutValue,
          },
        ],
      });

      // Prepare metadata for RevenueCat
      let metadata: Record<string, string> = {
        checkout_attempt_id: attempt.id,
        checkout_telemetry_version: String(CHECKOUT_TELEMETRY_VERSION),
        paywall_source: paywallSource,
        purchase_identity: isAnonymousCheckout ? "anonymous" : "identified",
      };

      const attribution = getAttributionData();
      Object.assign(metadata, buildOpenAIAdsPurchaseMetadata(attribution));
      for (const key of [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
      ] as const) {
        const value = attribution?.[key]?.trim();
        if (value) metadata[key] = value;
      }

      if (anonymousCheckoutProperties) {
        Object.entries(anonymousCheckoutProperties).forEach(([key, value]) => {
          metadata[key] = String(value);
        });
      }

      if (couponApplied && coupon) {
        metadata.coupon_code = coupon.toLowerCase();
      }

      metadata = await enrichRevenueCatMetadataWithInsertAffiliate(metadata);

      // Set PostHog ID if available
      let posthogUserId: string | undefined;
      try {
        const posthogId = posthog.get_distinct_id();
        if (posthogId) {
          posthogUserId = posthogId;
        }
      } catch (e) {
        logger.warn("Failed to get PostHog ID for attributes", { error: e });
      }

      const attributes = buildCheckoutSubscriberAttributes({
        email: user?.email,
        posthogUserId,
        couponCode: couponApplied ? coupon : undefined,
      });

      // Set attributes before purchase
      stage = "attributes";
      if (Object.keys(attributes).length > 0) {
        await setAttributes(attributes);
      }

      if (isAnonymousCheckout) {
        const anonymousAppUserId = await getRevenueCatAppUserId();
        if (!anonymousAppUserId) {
          throw new Error("Anonymous RevenueCat customer was not initialized.");
        }
        storePendingWebPurchase({
          anonymousAppUserId,
          redeemUrl: null,
          packageType,
          purchasedAtMs: Date.now(),
        });
      }

      // Purchase the package using RevenueCat SDK
      // Pass email to skip email collection step (faster checkout)
      stage = "sdk_purchase";
      checkout.startPurchase(attempt);
      const result = await purchasePackage(packageToPurchase, metadata, user?.email, {
        onStalled: () => handleCheckoutPending(attempt),
      });

      if (!checkout.finishSuccessfulPurchase(attempt)) return;

      logger.info("Purchase successful", {
        packageType,
        activeSubscriptions: result.customerInfo.activeSubscriptions,
        couponCode: couponApplied ? coupon : undefined,
      });
      if (user?.email) {
        invalidateRevenueCatSubscriptionCache(user.email);
      }

      if (isAnonymousCheckout) {
        const anonymousAppUserId = await getRevenueCatAppUserId();
        if (anonymousAppUserId) {
          storePendingWebPurchase({
            anonymousAppUserId,
            redeemUrl: result.redemptionInfo?.redeemUrl ?? null,
            packageType,
            purchasedAtMs: Date.now(),
          });
        }
      }

      const isTrial = offerMode === "trial" && packageType !== "weekly";
      trackRevenueCatConversionEvent({
        currency: checkoutCurrency,
        isTrial,
        itemId: checkoutItemId,
        itemName: REVENUECAT_CONFIG.packages[packageType].displayName,
        packageId: packageToPurchase?.identifier,
        paywallSource,
        source: "pricing_section",
        transactionId: `rc_client_${attempt.id}`,
        userEmail: user?.email,
        value: isTrial ? 0 : checkoutValue,
      });

      // Track Subscribe event to Meta Pixel
      try {
        if (typeof window !== "undefined" && (window as any).fbq) {
          (window as any).fbq("track", "Subscribe", {
            value: packageToPurchase?.product?.price || "0.00",
            currency: packageToPurchase?.product?.currencyCode || "USD",
            predicted_ltv: "0.00", // Defaulting to 0.00 as requested
          });
        }
      } catch (e) {
        logger.warn("Failed to track Meta Pixel Subscribe event", { error: e });
      }

      // Redirect to success page or call callback
      if (isAnonymousCheckout) {
        router.push(ANONYMOUS_PURCHASE_ACTIVATION_PATH);
      } else if (onPurchaseSuccess) {
        onPurchaseSuccess();
      } else {
        router.push("/success?source=purchase");
      }
    } catch (err: unknown) {
      const resolution = await checkout.handlePurchaseError(attempt, stage, err);
      if (resolution.status === "active") {
        completeRecoveredCheckout(attempt);
      } else if (resolution.message) {
        setError(resolution.message);
      }
      if (
        isAnonymousCheckout &&
        (resolution.status === "cancelled" || resolution.status === "failed")
      ) {
        clearPendingWebPurchase();
      }
    }
  };

  useEffect(() => {
    resumeCheckoutActionRef.current = (packageType) => handleSelectPackage(packageType, false);
  });

  useEffect(() => {
    if (
      !resumePackage ||
      !user?.email ||
      !offerings ||
      isInitializing ||
      !isSegmentResolved ||
      !hasAppliedSegmentDefault ||
      (hasExternalOfferOverride && !externalCouponProcessed) ||
      checkout.locked ||
      resumedPackageRef.current === resumePackage
    ) {
      return;
    }

    resumedPackageRef.current = resumePackage;

    if (typeof window !== "undefined") {
      const resumedUrl = new URL(window.location.href);
      resumedUrl.searchParams.delete("resume");
      resumedUrl.searchParams.delete("plan");
      window.history.replaceState(window.history.state, "", resumedUrl.toString());
    }

    void resumeCheckoutActionRef.current(resumePackage);
  }, [
    checkout.locked,
    externalCouponProcessed,
    hasAppliedSegmentDefault,
    hasExternalOfferOverride,
    isInitializing,
    isSegmentResolved,
    offerings,
    resumePackage,
    user?.email,
  ]);

  const activeOffering = getActiveOffering();
  const weeklyPricing = getPackageDisplayPricing({
    currency,
    discountRequested: couponApplied,
    packageToPurchase: findSelectableRevenueCatPackage({
      activeOffering,
      coupon,
      couponApplied,
      offerings,
      packageType: "weekly",
      preferredCurrency: currency,
    }),
    packageType: "weekly",
  });
  const monthlyPricing = getPackageDisplayPricing({
    currency,
    discountRequested: couponApplied,
    packageToPurchase: findSelectableRevenueCatPackage({
      activeOffering,
      coupon,
      couponApplied,
      offerings,
      packageType: "monthly",
      preferredCurrency: currency,
    }),
    packageType: "monthly",
  });
  const annualPricing = getPackageDisplayPricing({
    currency,
    discountRequested: couponApplied,
    packageToPurchase: findSelectableRevenueCatPackage({
      activeOffering,
      coupon,
      couponApplied,
      offerings,
      packageType: "annual",
      preferredCurrency: currency,
    }),
    packageType: "annual",
  });
  const hasVisibleDiscount =
    weeklyPricing.hasDiscountPrice ||
    monthlyPricing.hasDiscountPrice ||
    annualPricing.hasDiscountPrice;
  const promoDisplay = getPromoDisplayState({
    couponApplied,
    hasVisibleDiscount,
    showOfferToggle: showOfferToggleOnPromo,
  });
  const pricingByPackage = {
    weekly: weeklyPricing,
    monthly: monthlyPricing,
    annual: annualPricing,
  } satisfies Record<PackageType, typeof weeklyPricing>;
  const annualSavingsPercent = getComparableAnnualSavingsPercent({
    annualPricing,
    fallbackCurrency: currency,
    monthlyPricing,
  });
  const annualMonthlyEquivalent = formatMonthlyEquivalent(
    annualPricing.priceAmount,
    annualPricing.currencyCode
  );
  const reservationFirstName =
    typeof user?.user_metadata?.first_name === "string"
      ? user.user_metadata.first_name
      : typeof user?.user_metadata?.full_name === "string"
        ? user.user_metadata.full_name.split(" ")[0]
        : null;

  useEffect(() => {
    if (
      !paywallExperiment ||
      paywallExperiment.status !== "resolved" ||
      authLoading ||
      isInitializing ||
      !offerings ||
      !isSegmentResolved
    ) {
      return;
    }

    onExperimentOffersRendered?.({ offerMode, userIntentSegment });
  }, [
    authLoading,
    isInitializing,
    isSegmentResolved,
    offerMode,
    offerings,
    onExperimentOffersRendered,
    paywallExperiment,
    userIntentSegment,
  ]);

  // Loading skeleton
  if (authLoading || isInitializing || paywallExperiment?.status === "loading") {
    return (
      <div className={isCompactLanding ? "py-0" : "py-16 md:py-24"}>
        <div className={isCompactLanding ? "mx-auto max-w-6xl" : "mx-auto max-w-6xl px-4"}>
          {showHeader && (
            <div className="mb-12 text-center">
              <div className="mx-auto mb-4 h-10 w-64 rounded-lg bg-gray-700/50 motion-safe:animate-pulse" />
              <div className="mx-auto h-6 w-96 max-w-full rounded-lg bg-gray-700/50 motion-safe:animate-pulse" />
            </div>
          )}
          {paywallExperiment ? (
            <div
              className="rounded-2xl border border-white/15 bg-[#15161B] p-6 md:p-8"
              data-pricing-skeleton-layout="neutral"
            >
              <div className="h-3 w-32 rounded bg-white/10 motion-safe:animate-pulse" />
              <div className="mt-4 h-8 w-64 max-w-full rounded bg-white/10 motion-safe:animate-pulse" />
              <div className="mt-3 h-4 w-full rounded bg-white/10 motion-safe:animate-pulse" />
              <div className="mt-8 h-12 w-full rounded bg-white/10 motion-safe:animate-pulse" />
            </div>
          ) : (
            <div
              className={`grid md:grid-cols-3 ${isCompactLanding ? "gap-4 md:gap-6" : "gap-6 md:gap-8"}`}
            >
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-80 rounded-2xl bg-gray-800/50 p-6 motion-safe:animate-pulse"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={pricingSectionRef}
      className={`relative overflow-hidden ${isCompactLanding ? "py-0" : "py-16 md:py-24"}`}
    >
      {pricingCardVariant !== "landing" && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute right-0 top-0 h-[500px] w-[500px] rounded-full bg-orange-500/5 blur-3xl" />
        </div>
      )}

      <div className={`relative mx-auto max-w-6xl ${isCompactLanding ? "px-0" : "px-4"}`}>
        {/* Section Header */}
        {showHeader && (
          <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">{headerTitle}</h2>
            <p className="text-gray-400 text-lg">{headerSubtitle}</p>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-8 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-center"
          >
            {error}
          </motion.div>
        )}

        {(checkout.status === "pending_unconfirmed" ||
          checkout.status === "verifying" ||
          checkout.status === "unconfirmed") && (
          <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-auto mb-8 max-w-2xl rounded-lg border border-amber-400/40 bg-amber-400/10 p-4 text-center text-amber-100"
            role="status"
          >
            <p>
              Your checkout is taking longer than usual. Please keep it open and don&apos;t start
              another purchase.
            </p>
            <button
              type="button"
              onClick={handleVerifyCheckout}
              disabled={checkout.status === "verifying"}
              className="mt-3 rounded-md border border-amber-300/40 px-4 py-2 text-sm font-semibold transition-colors hover:bg-amber-300/10 disabled:opacity-60"
            >
              {checkout.status === "verifying" ? "Checking…" : "Check purchase status"}
            </button>
          </motion.div>
        )}

        {/* Pricing Cards */}
        {paywallExperiment ? (
          <div className={isCompactLanding ? "mb-5" : "mb-12"}>
            <PaywallExperimentPricingCards
              analyticsSource={analyticsSource}
              annualSavingsPercent={annualSavingsPercent}
              checkoutLocked={checkout.locked}
              checkoutStatus={checkout.status}
              cohortEmail={user?.email ?? ""}
              cohortFirstName={reservationFirstName}
              cohortLanguageCode={cohortLanguageCode}
              offerMode={offerMode}
              onBillingChange={(packageType) => {
                setSelectedBilling(packageType);
                trackEventWithAttribution(posthog, "paywall_billing_frequency_selected", {
                  ...analyticsProperties,
                  billing_frequency: packageType,
                  experiment_key: PAYWALL_GUIDED_ANCHOR_EXPERIMENT_KEY,
                  experiment_variant: paywallExperiment.variant,
                  offer_mode: offerMode,
                  paywall_layout: PAYWALL_GUIDED_ANCHOR_LAYOUTS[paywallExperiment.variant],
                  source: analyticsSource,
                  user_intent_segment: userIntentSegment,
                });
              }}
              onCohortAuthRequired={onCohortAuthRequired}
              onPurchase={handleSelectPackage}
              pricingByPackage={pricingByPackage}
              selectedBilling={selectedBilling}
              selectedPackage={checkout.selectedPackage}
              variant={paywallExperiment.variant}
            />
          </div>
        ) : (
          <div
            className={`mx-auto grid max-w-6xl grid-cols-1 md:grid-cols-3 ${
              isCompactLanding ? "mb-5 gap-4 md:gap-6" : "mb-12 gap-8 md:gap-8"
            }`}
          >
            <PricingCard
              variant={pricingCardVariant}
              packageType="weekly"
              currency={currency}
              onSelectAction={handleSelectPackage}
              disabled={checkout.locked}
              isLoading={
                checkout.selectedPackage === "weekly" &&
                (checkout.status === "preparing" || checkout.status === "awaiting_checkout")
              }
              isSelected={checkout.selectedPackage === "weekly"}
              showFreeTrial={false}
              overridePriceFormatted={weeklyPricing.priceFormatted}
              originalPriceFormatted={weeklyPricing.originalPriceFormatted}
              discountLabel={weeklyPricing.hasDiscountPrice ? "Discount applied" : undefined}
              promotionalPeriod={weeklyPricing.promotionalPeriod}
            />
            <PricingCard
              variant={pricingCardVariant}
              packageType="monthly"
              currency={currency}
              onSelectAction={handleSelectPackage}
              disabled={checkout.locked}
              isLoading={
                checkout.selectedPackage === "monthly" &&
                (checkout.status === "preparing" || checkout.status === "awaiting_checkout")
              }
              isSelected={checkout.selectedPackage === "monthly"}
              showFreeTrial={offerMode === "trial" && monthlyPricing.showFreeTrial}
              overridePriceFormatted={monthlyPricing.priceFormatted}
              originalPriceFormatted={monthlyPricing.originalPriceFormatted}
              discountLabel={monthlyPricing.hasDiscountPrice ? "Discount applied" : undefined}
              promotionalPeriod={monthlyPricing.promotionalPeriod}
            />
            <PricingCard
              variant={pricingCardVariant}
              packageType="annual"
              currency={currency}
              onSelectAction={handleSelectPackage}
              disabled={checkout.locked}
              isLoading={
                checkout.selectedPackage === "annual" &&
                (checkout.status === "preparing" || checkout.status === "awaiting_checkout")
              }
              isSelected={checkout.selectedPackage === "annual"}
              showFreeTrial={offerMode === "trial" && annualPricing.showFreeTrial}
              overridePriceFormatted={
                pricingCardVariant === "landing" && annualMonthlyEquivalent
                  ? annualMonthlyEquivalent
                  : annualPricing.priceFormatted
              }
              originalPriceFormatted={
                pricingCardVariant === "landing" ? undefined : annualPricing.originalPriceFormatted
              }
              periodTextOverride={pricingCardVariant === "landing" ? "per month" : undefined}
              billingDetail={
                pricingCardVariant === "landing"
                  ? `${annualPricing.priceFormatted} billed yearly`
                  : undefined
              }
              discountLabel={annualPricing.hasDiscountPrice ? "Discount applied" : undefined}
              promotionalPeriod={annualPricing.promotionalPeriod}
            />
          </div>
        )}

        {/* Promo Code Section */}
        {promoDisplay.showSection && (
          <div className={isCompactLanding ? "mt-3 text-center" : "mb-12 mt-8 text-center"}>
            {!couponApplied && (
              <button
                disabled={checkout.locked}
                onClick={() => {
                  setShowOfferToggleOnPromo(true);
                  setShowCouponInput((prev) => !prev);
                }}
                className={`text-sm font-medium transition-colors ${
                  pricingCardVariant === "landing"
                    ? "text-gray-400 hover:text-[#FD954F]"
                    : "text-gray-400 hover:text-orange-400"
                }`}
              >
                Have a promo code?
              </button>
            )}
            {promoDisplay.showConfirmation && (
              <div className="bg-green-500/20 border border-green-500/50 rounded-lg px-4 py-2 inline-block">
                <span className="text-green-400 font-semibold text-sm">
                  Limited-time promotional pricing applied
                </span>
              </div>
            )}

            {showOfferToggleOnPromo && (
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <button
                  disabled={checkout.locked}
                  onClick={handleSwitchToTrial}
                  className={`rounded-xl px-4 py-1.5 text-sm font-medium transition-colors ${
                    offerMode === "trial"
                      ? pricingCardVariant === "landing"
                        ? "bg-[#FD954F] text-[#1C1C1E]"
                        : "bg-orange-600 text-white"
                      : pricingCardVariant === "landing"
                        ? "bg-[#15161B] text-gray-300 hover:text-white"
                        : "bg-gray-800 text-gray-300 hover:text-white"
                  }`}
                >
                  3-Day Trial
                </button>
                <button
                  disabled={checkout.locked}
                  onClick={() => void handleSwitchToDiscount()}
                  className={`rounded-xl px-4 py-1.5 text-sm font-medium transition-colors ${
                    offerMode === "discount"
                      ? pricingCardVariant === "landing"
                        ? "bg-[#FD954F] text-[#1C1C1E]"
                        : "bg-green-600 text-white"
                      : pricingCardVariant === "landing"
                        ? "bg-[#15161B] text-gray-300 hover:text-white"
                        : "bg-gray-800 text-gray-300 hover:text-white"
                  }`}
                >
                  Up to 33% Off
                </button>
              </div>
            )}

            {/* Expandable Coupon Input */}
            {showCouponInput && !couponApplied && (
              <motion.div
                initial={shouldReduceMotion ? false : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
                className="mt-4 flex gap-2 justify-center max-w-sm mx-auto"
              >
                <input
                  disabled={checkout.locked}
                  type="text"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  placeholder="Enter promo code"
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm text-white focus:outline-none ${
                    pricingCardVariant === "landing"
                      ? "border-white/15 bg-[#15161B] placeholder:text-[#AEAEB2] focus:border-[#FD954F]"
                      : "border-gray-700/60 bg-gray-800/60 placeholder-gray-500 focus:border-orange-500"
                  }`}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      void applyCoupon().then((applied) => {
                        setOfferMode(applied ? "discount" : "trial");
                      });
                    }
                  }}
                />
                <button
                  disabled={checkout.locked}
                  onClick={() =>
                    void applyCoupon().then((applied) => {
                      setOfferMode(applied ? "discount" : "trial");
                    })
                  }
                  className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                    pricingCardVariant === "landing"
                      ? "bg-[#FD954F] text-[#1C1C1E] hover:bg-[#F28747]"
                      : "bg-orange-600 text-white hover:bg-orange-700"
                  }`}
                >
                  Apply
                </button>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
